import React, { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  icon: string;
}

interface InventoryItem {
  itemId: string;
  quantity: number;
  definition: ItemDefinition | null;
}

interface RiftRelicPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  riftStormId: string;
  riftStormTier: string;
  lootMultiplier: number;
  xpMultiplier: number;
}

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const RELIC_TIERS: Record<string, { name: string; description: string }> = {
  'rift_relic_worn': { name: 'Worn Rift Relic', description: 'A weathered void relic. Can be cracked in a Rift Storm for rewards.' },
  'rift_relic_intact': { name: 'Intact Rift Relic', description: 'A well-preserved void relic. Crack it in a Rift Storm for better rewards.' },
  'rift_relic_radiant': { name: 'Radiant Rift Relic', description: 'A glowing void relic pulsing with power. Crack it in a Rift Storm for rare rewards.' },
  'rift_relic_exceptional': { name: 'Exceptional Rift Relic', description: 'A legendary void relic crackling with void energy. Crack it in a Rift Storm for legendary rewards.' },
};

const renderItemIcon = (icon: string | undefined, name: string): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  const resolvedSrc = icon && icon.startsWith('/assets')
    ? `${publicUrl}${icon}`
    : icon && (icon.startsWith('http://') || icon.startsWith('https://'))
    ? icon
    : `${publicUrl}/assets/unknown.png`;
  
  const fallbackChain = [
    `${publicUrl}/assets/unknown.png`,
  ];
  let currentFallbackIndex = 0;
  
  return (
    <img
      src={resolvedSrc}
      alt={name}
      style={{ width: 32, height: 32, objectFit: 'contain' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        currentFallbackIndex++;
        
        if (currentFallbackIndex < fallbackChain.length) {
          const nextFallback = fallbackChain[currentFallbackIndex];
          if (target.src !== nextFallback) {
            target.src = nextFallback;
            return;
          }
        }
        
        target.onerror = null;
      }}
    />
  );
};

export const RiftRelicPanel: React.FC<RiftRelicPanelProps> = ({
  socket,
  isOpen,
  onClose,
  riftStormId,
  riftStormTier,
  lootMultiplier,
  xpMultiplier,
}) => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [selectedRelic, setSelectedRelic] = useState<InventoryItem | null>(null);
  const [isCracking, setIsCracking] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [crackedLoot, setCrackedLoot] = useState<Array<{ itemId: string; name: string; quantity: number }> | null>(null);
  const { handleHeaderMouseDown, style: dragStyle } = useDraggablePanel(200, 150);

  // Keyboard handler for closing with Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Request inventory data on open
  useEffect(() => {
    if (isOpen && socket) {
      socket.emit('inventory:get');
    }
  }, [isOpen, socket]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleInventoryUpdate = (data: { items: InventoryItem[] }): void => {
      setInventory(data.items);
    };

    const handleRelicCracked = (data: { relicItemId: string; stormId: string; loot: Array<{ itemId: string; name: string; quantity: number }> }): void => {
      setIsCracking(false);
      setCrackedLoot(data.loot);
      setSelectedRelic(null);
      setMessage(`Successfully cracked ${RELIC_TIERS[data.relicItemId]?.name || data.relicItemId}!`);
      socket.emit('inventory:get'); // Refresh inventory
      setTimeout(() => setMessage(''), 5000);
    };

    const handleRelicError = (data: { message: string }): void => {
      setIsCracking(false);
      setMessage(data.message);
      setTimeout(() => setMessage(''), 5000);
    };

    socket.on('inventory:data', handleInventoryUpdate);
     socket.on('riftRelic:cracked', handleRelicCracked);
     socket.on('riftRelic:error', handleRelicError);

     return () => {
       socket.off('inventory:data', handleInventoryUpdate);
       socket.off('riftRelic:cracked', handleRelicCracked);
       socket.off('riftRelic:error', handleRelicError);
    };
  }, [socket]);

  if (!isOpen) return null;

  const riftRelics = inventory.filter(item =>
    item.itemId.startsWith('rift_relic_') && item.quantity > 0
  );

  const handleCrackRelic = (): void => {
    if (!selectedRelic || !socket) return;

    setIsCracking(true);
    setMessage('');
    setCrackedLoot(null);

    socket.emit('riftRelic:crack', {
      relicItemId: selectedRelic.itemId,
      stormId: riftStormId,
    });
  };

  const handleCloseLoot = (): void => {
    setCrackedLoot(null);
  };

  const renderRelicItem = (relic: InventoryItem): JSX.Element => {
    const isSelected = selectedRelic?.itemId === relic.itemId;
    const rarityColor = RARITY_COLORS[relic.definition?.rarity || 'common'];

    return (
      <div
        key={relic.itemId}
        className={`rift-relic-item ${isSelected ? 'selected' : ''}`}
        style={{
          borderLeftColor: rarityColor,
          borderLeftWidth: '3px',
          cursor: 'pointer',
          padding: '8px',
          marginBottom: '4px',
          backgroundColor: isSelected ? '#2d3748' : '#1a202c',
          border: '1px solid #4a5568',
        }}
        onClick={() => setSelectedRelic(relic)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderItemIcon(relic.definition?.icon, relic.definition?.name || relic.itemId)}
          <div style={{ flex: 1 }}>
            <div style={{ color: rarityColor, fontWeight: 'bold' }}>
              {relic.definition?.name || RELIC_TIERS[relic.itemId]?.name || relic.itemId}
            </div>
            <div style={{ fontSize: '12px', color: '#a0aec0' }}>
              {relic.definition?.description || RELIC_TIERS[relic.itemId]?.description || 'A mysterious relic from the void.'}
            </div>
            <div style={{ fontSize: '11px', color: '#718096' }}>
              Quantity: {relic.quantity}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLootResults = (): JSX.Element => {
    if (!crackedLoot) return <></>;

    return (
      <div className="rift-relic-loot-results" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}>
        <div style={{
          backgroundColor: '#1a202c',
          border: '1px solid #4a5568',
          padding: '20px',
          maxWidth: '300px',
          textAlign: 'center',
        }}>
          <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>Relic Cracked!</h3>
          <div style={{ marginBottom: '16px' }}>
            {crackedLoot.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
                padding: '8px',
                backgroundColor: '#2d3748',
              }}>
                {renderItemIcon(item.itemId, item.name)}
                <div>
                  <div style={{ color: RARITY_COLORS['uncommon'], fontWeight: 'bold' }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                    Quantity: {item.quantity}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={handleCloseLoot}
            style={{
              backgroundColor: '#3182ce',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="rift-relic-panel" style={{
      position: 'fixed',
      zIndex: 1000,
      width: '400px',
      height: '500px',
      backgroundColor: '#1a202c',
      border: '1px solid #4a5568',
      ...dragStyle,
    }}>
      {/* Header */}
      <div
        className="rift-relic-panel-header"
        onMouseDown={handleHeaderMouseDown}
        style={{
          cursor: 'grab',
          userSelect: 'none',
          padding: '8px 12px',
          backgroundColor: '#2d3748',
          borderBottom: '1px solid #4a5568',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2 style={{ margin: 0, color: '#ffd700', fontSize: '16px' }}>
          Rift Relic Cracker
        </h2>
        <button
          className="close-btn"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#a0aec0',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '0',
          }}
        >
          ×
        </button>
      </div>

      {/* Storm Info */}
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#1a202c',
        borderBottom: '1px solid #4a5568',
      }}>
        <div style={{ fontSize: '12px', color: '#a0aec0' }}>
          Rift Storm: {riftStormTier.toUpperCase()}
        </div>
        <div style={{ fontSize: '11px', color: '#68d391' }}>
          Loot: +{Math.round((lootMultiplier - 1) * 100)}% | XP: +{Math.round((xpMultiplier - 1) * 100)}%
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: message.includes('Successfully') ? '#2d5a27' : '#5a2d2d',
          color: message.includes('Successfully') ? '#68d391' : '#fc8181',
          fontSize: '12px',
          textAlign: 'center',
        }}>
          {message}
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '12px', height: 'calc(100% - 120px)', overflowY: 'auto' }}>
        {riftRelics.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#a0aec0',
            padding: '40px',
            fontSize: '14px',
          }}>
            No Rift Relics found.
            <br />
            <span style={{ fontSize: '12px' }}>
              Defeat enemies in Rift Storms to obtain relics.
            </span>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '12px', fontSize: '14px', color: '#e2e8f0' }}>
              Select a relic to crack:
            </div>
            {riftRelics.map(renderRelicItem)}

            {selectedRelic && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button
                  onClick={handleCrackRelic}
                  disabled={isCracking}
                  style={{
                    backgroundColor: isCracking ? '#4a5568' : '#3182ce',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    cursor: isCracking ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {isCracking ? 'Cracking...' : `Crack ${RELIC_TIERS[selectedRelic.itemId]?.name || selectedRelic.itemId}`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Loot Results Overlay */}
      {renderLootResults()}
    </div>
  );
};
